import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { formatNaira } from "@/lib/mock-data";
import { analyticsApi, ApiError, type AnalyticsDaily, type AnalyticsSummary } from "@/lib/api";
import { Download, TrendingUp, TrendingDown, MapPinned } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ZoneMappingDialog } from "@/components/parcelio/zone-mapping-dialog";
import { cn } from "@/lib/utils";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

const EMPTY_SUMMARY: AnalyticsSummary = {
  active_deliveries: 0,
  completed_today: 0,
  on_time_rate: 0,
  active_drivers: 0,
  revenue_today_kobo: 0,
  exceptions: 0,
};

export default function Analytics() {
  const token = useAuth((s) => s.session?.accessToken);
  const isPersonal = useAuth((s) => s.session?.kind === "personal");
  const [summary, setSummary] = useState<AnalyticsSummary>(EMPTY_SUMMARY);
  const [daily, setDaily] = useState<AnalyticsDaily>({ deliveries: [], revenue_kobo: [] });
  const [postcodeZones, setPostcodeZones] = useState<{ zone: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      analyticsApi
        .summary(token)
        .then(setSummary)
        .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load analytics summary")),
      analyticsApi
        .daily(token)
        .then(setDaily)
        .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load daily analytics")),
      // Best-effort — stays empty (section hidden) if postcode enrichment
      // was never enabled or no parcels resolved one yet.
      analyticsApi
        .postcodeZones(token)
        .then((r) => setPostcodeZones(r.zones))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [token]);

  const dailyDeliveries = daily.deliveries.map((d) => d.count);
  const dailyRevenueNaira = daily.revenue_kobo.map((r) => r.amount_kobo / 100);
  const totalDeliveries = dailyDeliveries.reduce((a, b) => a + b, 0);
  const totalRevenueNaira = dailyRevenueNaira.reduce((a, b) => a + b, 0);

  return (
    <>
      <Topbar
        title="Analytics"
        subtitle={isPersonal ? "Your deliveries & spend" : "Delivery performance & revenue"}
        actions={<Button variant="outline" size="sm"><Download className="mr-1.5 h-4 w-4" /> Export</Button>}
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        {loading ? <LoadingBlock label="Loading analytics…" /> : <>
        {/* Summary */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Deliveries (14d)" value={String(totalDeliveries)} delta="+12.4%" positive />
          <SummaryCard label="Spend (14d)" value={formatNaira(totalRevenueNaira)} delta="+18.1%" positive />
          <SummaryCard label="On-time rate" value={`${summary.on_time_rate}%`} delta="+2.1%" positive />
          {isPersonal ? (
            <SummaryCard label="Vehicles" value={String(summary.active_drivers)} delta="" positive />
          ) : (
            <SummaryCard label="Exceptions" value={String(summary.exceptions)} delta="-3" positive />
          )}
        </section>

        {/* Charts */}
        <section className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Deliveries per day" data={dailyDeliveries.length ? dailyDeliveries : [0]} tone="info" formatter={(v) => `${v}`} />
          <ChartCard
            title="Spend per day (₦'000)"
            data={(dailyRevenueNaira.length ? dailyRevenueNaira : [0]).map((v) => Math.round(v / 1000))}
            tone="success"
            formatter={(v) => `₦${v}k`}
          />
        </section>

        {/* NIPOST postcode zone density — only shows once parcels have
            actually resolved postcodes (enrichment toggle must be on). */}
        {postcodeZones.length > 0 ? (
          <section className="surface-card p-5">
            <div className="flex items-center gap-2">
              <MapPinned className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Delivery density by postcode zone</h3>
              <ZoneMappingDialog />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Pickup + dropoff legs grouped by NIPOST state zone (e.g. "LA" = Lagos)
            </p>
            <ResponsiveContainer width="100%" height={220} className="mt-4">
              <BarChart data={postcodeZones}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="zone" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="var(--color-info)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>
        ) : null}

        {/* Split composition — platform-wide mock breakdown, not meaningful
            for a single personal account */}
        {isPersonal ? null : <section className="grid gap-4 lg:grid-cols-2">
          <div className="surface-card p-5">
            <h3 className="text-sm font-semibold">Delivery status breakdown</h3>
            <div className="mt-4 space-y-3">
              {[
                { l: "Delivered", v: 74, tone: "bg-success" },
                { l: "In transit", v: 12, tone: "bg-info" },
                { l: "Out for delivery", v: 8, tone: "bg-warning" },
                { l: "Exception", v: 4, tone: "bg-destructive" },
                { l: "Pending", v: 2, tone: "bg-muted-foreground/50" },
              ].map((r) => (
                <div key={r.l}>
                  <div className="mb-1 flex justify-between text-xs">
                    <span className="text-muted-foreground">{r.l}</span>
                    <span className="font-medium">{r.v}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className={cn("h-full rounded-full", r.tone)} style={{ width: `${r.v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="surface-card p-5">
            <h3 className="text-sm font-semibold">Top drivers this week</h3>
            <ul className="mt-4 space-y-3">
              {[
                { n: "Chioma Nwosu", v: 92, r: "4.95" },
                { n: "Amaka Obi", v: 84, r: "4.9" },
                { n: "Kunle Ojo", v: 71, r: "4.85" },
                { n: "Tunde Bakare", v: 58, r: "4.7" },
                { n: "Femi Adeyemi", v: 42, r: "4.6" },
              ].map((d) => (
                <li key={d.n} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary">
                    {d.n.split(" ").map((x) => x[0]).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{d.n}</div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${d.v}%` }} />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{d.v}</div>
                    <div className="text-[10px] text-muted-foreground">★ {d.r}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>}
        </>}
      </div>
    </>
  );
}

function SummaryCard({ label, value, delta, positive }: { label: string; value: string; delta: string; positive?: boolean }) {
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div className="surface-card p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-bold tracking-tight">{value}</div>
      <div className={cn("mt-1 inline-flex items-center gap-1 text-xs font-medium", positive ? "text-success" : "text-destructive")}>
        <Icon className="h-3.5 w-3.5" /> {delta}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  data,
  tone,
  formatter,
}: {
  title: string;
  data: number[];
  tone: "info" | "success";
  formatter: (v: number) => string;
}) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const w = 600;
  const h = 180;
  const stepX = w / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = h - ((v - min) / (max - min || 1)) * (h - 20) - 10;
    return [x, y] as const;
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  const stroke = tone === "info" ? "var(--color-info)" : "var(--color-success)";
  const fill = tone === "info" ? "var(--color-info)" : "var(--color-success)";
  return (
    <div className="surface-card p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">Last 14 days</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="mt-4 w-full">
        <defs>
          <linearGradient id={`grad-${tone}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={fill} stopOpacity="0.35" />
            <stop offset="100%" stopColor={fill} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#grad-${tone})`} />
        <path d={path} stroke={stroke} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill={stroke} />
        ))}
      </svg>
      <div className="mt-2 flex justify-between text-xs text-muted-foreground">
        <span>{formatter(data[0])}</span>
        <span>{formatter(data[data.length - 1])}</span>
      </div>
    </div>
  );
}
